// @givmo/board — the connect screen, before Google's consent screen, and the answer after it
// (CBG-CAL1 slice 1d; the approved frames `connect-*`).
//
// One dialog for every way in: an officer's own calendar (Your settings, Board's card, or inline from
// the schedule dialog) and the board account (an Owner, from Board's card). It says what happens in three
// short facts (what Google will ask, what someone's own AI assistant can read (CBG-CAL5), what
// everyone invited can do), offers the board account only to someone who may connect it, and hands
// off to Google with the design system's own provider button; the fine print sits behind "How this
// works". The answer opens in the SAME dialog (`use-calendar-connect.ts`): connected, or not, and
// why.

import { CheckCircle2 } from 'lucide-react';
import { useState } from 'react';
import {
  Button,
  Dialog,
  Icon,
  ProviderButton,
  RadioTile,
  RadioTileGroup,
} from '@givmo/design-system';
import { HowThisWorks } from '../../components/fine-print';
import type { ConnectOutcome } from '../../calendar/calendar-connect';
import type { CalendarConnectFlow } from '../../calendar/use-calendar-connect';
import { boardAddressExample, calendarWords as w } from '../../calendar/calendar-words';
import { BODY, DIALOG_TITLE, SMALL, STRONG_LINE } from './calendar-parts';

export interface ConnectDialogProps {
  /** `board-account` from Board's card; `mine` everywhere one's own calendar is connected. */
  mode: 'mine' | 'board-account';
  /**
   * Opened from a row that already names its account (Board's card, CBG-UX14): the screen goes
   * straight to that account, never offering the "Whose calendar" choice. Absent: offered as before.
   */
  fromRow?: boolean;
  /** The viewer holds `board_calendar:manage`: they may connect the board account, and change the settings. */
  canManage: boolean;
  /** A board account is already connected (so it is not offered again). */
  boardAccountExists: boolean;
  /** The domain the board-account example borrows, or null (`organizationDomain`). */
  exampleDomain: string | null;
  /** The organization's name, for the invitation title in the fine print. */
  orgName: string;
  flow: CalendarConnectFlow;
  /** Runs when a full-page trip must leave (no pop-up): keep what the page would lose. */
  beforeRedirect?: () => void;
  onClose: () => void;
}

export function ConnectDialog(props: ConnectDialogProps) {
  const { flow, mode, onClose } = props;
  const label = mode === 'board-account' ? w.connectBoardTitle : w.connectTitle;
  return (
    <Dialog.Root open onOpenChange={(next) => !next && onClose()}>
      <Dialog.Content
        aria-label={label}
        width={448}
        className="flex flex-col gap-16"
        testId="calendar-connect-dialog"
      >
        {flow.outcome === null ? (
          <BeforeGoogle {...props} />
        ) : (
          <ConnectAnswer
            outcome={flow.outcome}
            boardAccount={mode === 'board-account'}
            onClose={onClose}
            onAgain={flow.reset}
          />
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}

function BeforeGoogle({
  mode,
  fromRow = false,
  canManage,
  boardAccountExists,
  exampleDomain,
  orgName,
  flow,
  beforeRedirect,
}: ConnectDialogProps) {
  const example = boardAddressExample(exampleDomain);
  // The board account is offered only to someone who may connect it, only while there is none, and
  // never over a row that already named the account being connected.
  const offersChoice = mode === 'mine' && !fromRow && canManage && !boardAccountExists;
  const [choice, setChoice] = useState<'mine' | 'board'>('mine');
  const forBoard = mode === 'board-account' || (offersChoice && choice === 'board');
  const how = [
    w.howTitle(orgName),
    w.howCount,
    ...(forBoard ? [] : [w.howDelegates, w.howHandover]),
  ];

  return (
    <>
      <div className="flex flex-col gap-6">
        <Dialog.Title className={DIALOG_TITLE}>
          {mode === 'board-account' ? w.connectBoardTitle : w.connectTitle}
        </Dialog.Title>
        <p className={BODY}>
          {mode === 'board-account' ? w.connectBoardLede(example) : w.connectLede}
        </p>
      </div>

      {offersChoice ? (
        <RadioTileGroup
          value={choice}
          onValueChange={(next) => setChoice(next === 'board' ? 'board' : 'mine')}
          aria-label={w.choiceGroup}
          testId="calendar-connect-choice"
        >
          <RadioTile value="mine" label={w.choiceMine} description={w.choiceMineDescription} />
          <RadioTile
            value="board"
            label={w.choiceBoard}
            description={w.choiceBoardDescription(example)}
          />
        </RadioTileGroup>
      ) : null}

      <ul
        className="flex flex-col gap-10 border-y border-border-subtle py-14"
        data-testid="calendar-connect-facts"
      >
        <li className={BODY}>{forBoard ? w.factAccessBoard : w.factAccess}</li>
        {/* CBG-CAL5: what someone's own AI assistant can read, said before anyone connects. */}
        <li className={BODY} data-testid="calendar-connect-assistant">
          {w.factAssistant}
        </li>
        <li className={BODY}>{canManage ? w.factDirectorsOwner : w.factDirectorsOfficer}</li>
      </ul>

      <div className="flex flex-col gap-10">
        {/* The step people get wrong, right where they take it. */}
        {forBoard ? (
          <p className={STRONG_LINE} data-testid="calendar-connect-board-sign-in">
            {w.howBoardSignIn}
          </p>
        ) : null}
        <ProviderButton
          provider="google"
          label={w.continueGoogle}
          loading={flow.starting}
          onClick={() =>
            flow.begin({ accountKind: forBoard ? 'board_account' : 'personal' }, { beforeRedirect })
          }
          testId="calendar-connect-google"
        />
      </div>

      <div>
        <HowThisWorks testId="calendar-connect-how" lines={how} />
      </div>
    </>
  );
}

/** The answer after Google: connected, or not and why (`connect-done`, `connect-refused`, …). */
export function ConnectAnswer({
  outcome,
  boardAccount,
  onClose,
  onAgain,
}: {
  outcome: ConnectOutcome;
  boardAccount: boolean;
  onClose: () => void;
  onAgain: () => void;
}) {
  if (outcome.kind === 'connected') {
    // Core's field is optional (Social's accounts carry none); a calendar's always has one.
    const email = outcome.connection.account_email ?? '';
    const isBoardAccount = boardAccount || outcome.connection.account_kind === 'board_account';
    return (
      <>
        <div className="flex flex-col items-start gap-10">
          <span className="text-success-600">
            <Icon icon={CheckCircle2} size={28} aria-hidden />
          </span>
          <Dialog.Title className={DIALOG_TITLE}>
            {isBoardAccount ? w.connectedBoardTitle : w.connectedTitle}
          </Dialog.Title>
          <p className={BODY} data-testid="calendar-connect-connected">
            {isBoardAccount ? w.connectedBoardBody(email) : w.connectedBody(email)}
          </p>
          {outcome.connection.is_consumer_account ? (
            <p className={SMALL} data-testid="calendar-connect-personal-account">
              {w.personalAccount}
            </p>
          ) : null}
        </div>
        <div className="flex justify-end">
          <Button appearance="saas" tone="primary" onClick={onClose} testId="calendar-connect-done">
            {w.done}
          </Button>
        </div>
      </>
    );
  }

  const reason =
    outcome.kind === 'refused'
      ? w.refused
      : outcome.kind === 'expired'
        ? w.expired
        : outcome.kind === 'unticked'
          ? w.unticked
          : outcome.kind === 'workspace-blocked'
            ? w.workspaceBlocked
            : outcome.kind === 'not-connected'
              ? (w.notConnected[outcome.code] ?? w.failed)
              : (outcome.message ?? w.failed);
  return (
    <>
      <div className="flex flex-col gap-6">
        <Dialog.Title className={DIALOG_TITLE}>{w.notConnectedTitle}</Dialog.Title>
        <p className={BODY} data-testid="calendar-connect-not-connected">
          {reason}
        </p>
      </div>
      <div className="flex justify-end gap-10">
        <Button
          appearance="saas"
          tone="secondary"
          onClick={onClose}
          testId="calendar-connect-close"
        >
          {w.close}
        </Button>
        {outcome.kind === 'workspace-blocked' ? null : (
          <Button
            appearance="saas"
            tone="primary"
            onClick={onAgain}
            testId="calendar-connect-again"
          >
            {outcome.kind === 'expired' ? w.startAgain : w.tryAgain}
          </Button>
        )}
      </div>
    </>
  );
}
